/**
 * Storezone — Progress Context
 *
 * Subscribes to the progress-store singleton and exposes
 * the loading state to the GlobalProgressBar component.
 *
 * The API client calls progressStore.increment/decrement directly —
 * this context only reads from the store so there is no circular dependency.
 */

import {
  createContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'

import { progressStore } from '@/lib/progress-store'

// ============================================================
// Types
// ============================================================

export interface ProgressContextValue {
  /** True when at least one request is in flight */
  isLoading: boolean
  /** Number of active requests */
  requestCount: number
}

// ============================================================
// Context
// ============================================================

export const ProgressContext = createContext<ProgressContextValue>({
  isLoading: false,
  requestCount: 0,
})

// ============================================================
// Provider
// ============================================================

export function ProgressProvider({ children }: { children: ReactNode }) {
  const [requestCount, setRequestCount] = useState(progressStore.getCount())

  useEffect(() => {
    const unsubscribe = progressStore.subscribe(setRequestCount)
    return unsubscribe
  }, [])

  return (
    <ProgressContext.Provider
      value={{ isLoading: requestCount > 0, requestCount }}
    >
      {children}
    </ProgressContext.Provider>
  )
}
